import { useEffect, useMemo, useRef, useState } from "react";

import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { environmentSelector } from "Reducers/environment";
import { environmentServicesSelector } from "Reducers/metrics/services.selector";
import { Poller } from "src/organization/pages/project/environment/metrics/utils/polling/poller";
import { MetricsPollType } from "src/organization/pages/project/environment/metrics/utils/polling/type";
import { useAppSelector } from "Store/hooks";

import { ResourceType } from "../types";

import { parseGridResource } from "./grid.utils";

type Props = Omit<MetricsPollType<ResourceType[]>, "timeframe">;

const usePoll = ({ onChange, onLoadingChange }: Props) => {
  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const [resource, setResource] = useState<ResourceType[]>();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const betterServices = useAppSelector(state =>
    environmentServicesSelector(state, organizationId, projectId, environmentId)
  );

  const data = useMemo(() => {
    const data = parseGridResource(resource, betterServices);
    return data;
  }, [betterServices, resource]);

  const poller = useRef<Poller>(
    new Poller<ResourceType[]>({
      onChange: resource => {
        onChange(resource);
        setResource(resource);
      },
      onError: () => onChange(),
      onLoadingChange
    })
  ).current;

  const metricsLink = environment?.getLinks()?.["#metrics"]?.[0] as
    | { collection?: string; href: string }
    | undefined;

  useEffect(() => {
    return () => poller.stopPolling();
  }, [poller]);

  return {
    data,
    poller,
    metricsLink
  };
};

export default usePoll;
